import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cancellationTicketService, CancellationTicketRow } from '../../services/dashboardService';

type Props = {
  eventId: string;
  eventTitle?: string;
};

const BUCKET_CHIP: Record<
  string,
  { label: string; color: 'success' | 'warning' | 'error' | 'default' | 'info' }
> = {
  eligible_with_balance: { label: 'Elegível + saldo', color: 'success' },
  out_of_policy: { label: 'Fora da política', color: 'warning' },
  insufficient_balance: { label: 'Sem saldo', color: 'error' },
  withdrawal_in_progress: { label: 'Saque em andamento', color: 'error' },
  registration_not_found: { label: 'Inscrição não achada', color: 'default' },
};

function formatWhen(value?: string | null) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('pt-BR');
  } catch {
    return '—';
  }
}

const EventWorkspaceAtendimentoTab: React.FC<Props> = ({ eventId }) => {
  const queryClient = useQueryClient();
  const [actionTicket, setActionTicket] = useState<{
    ticket: CancellationTicketRow;
    mode: 'approve' | 'reject';
  } | null>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['event-cancellation-tickets', eventId],
    queryFn: () => cancellationTicketService.listByEvent(eventId, 'all'),
  });

  const tickets = data?.tickets ?? [];
  const pendingCount = data?.pendingCount ?? 0;

  const pending = useMemo(
    () => tickets.filter((t) => t.status === 'opened' && (t.parishDecision || 'pending') === 'pending'),
    [tickets]
  );
  const others = useMemo(
    () => tickets.filter((t) => !(t.status === 'opened' && (t.parishDecision || 'pending') === 'pending')),
    [tickets]
  );

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['event-cancellation-tickets', eventId] });
  };

  const approveMutation = useMutation({
    mutationFn: () =>
      cancellationTicketService.approve(eventId, actionTicket!.ticket._id, note.trim() || undefined),
    onSuccess: () => {
      setActionTicket(null);
      setNote('');
      setError(null);
      invalidate();
    },
    onError: (err: any) => {
      setError(err?.response?.data?.message || 'Não foi possível aprovar o reembolso.');
    },
  });

  const rejectMutation = useMutation({
    mutationFn: () =>
      cancellationTicketService.reject(eventId, actionTicket!.ticket._id, note.trim() || undefined),
    onSuccess: () => {
      setActionTicket(null);
      setNote('');
      setError(null);
      invalidate();
    },
    onError: (err: any) => {
      setError(err?.response?.data?.message || 'Não foi possível recusar o pedido.');
    },
  });

  const busy = approveMutation.isPending || rejectMutation.isPending;

  const renderRow = (ticket: CancellationTicketRow, showActions: boolean) => {
    const bucket = ticket.eligibilityBucket
      ? BUCKET_CHIP[ticket.eligibilityBucket] || {
          label: ticket.eligibilityBucket,
          color: 'default' as const,
        }
      : null;
    const canApprove = ticket.eligibilityBucket === 'eligible_with_balance';

    return (
      <TableRow key={ticket._id} hover>
        <TableCell>
          <Typography variant="body2" fontWeight={600}>
            {ticket.email}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Aberto em {formatWhen(ticket.verifiedAt)}
          </Typography>
        </TableCell>
        <TableCell sx={{ maxWidth: 280 }}>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {ticket.reason}
          </Typography>
        </TableCell>
        <TableCell>
          {bucket ? <Chip size="small" label={bucket.label} color={bucket.color} /> : '—'}
          {ticket.eligibilityMessage ? (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              {ticket.eligibilityMessage}
            </Typography>
          ) : null}
        </TableCell>
        <TableCell>
          <Chip
            size="small"
            label={
              ticket.parishDecision === 'approved'
                ? 'Aprovado'
                : ticket.parishDecision === 'rejected'
                  ? 'Recusado'
                  : ticket.status === 'handled'
                    ? 'Tratado'
                    : 'Pendente'
            }
            color={
              ticket.parishDecision === 'approved'
                ? 'success'
                : ticket.parishDecision === 'rejected'
                  ? 'error'
                  : 'default'
            }
            variant="outlined"
          />
        </TableCell>
        <TableCell align="right">
          {showActions ? (
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                size="small"
                color="error"
                variant="outlined"
                onClick={() => {
                  setError(null);
                  setNote('');
                  setActionTicket({ ticket, mode: 'reject' });
                }}
              >
                Recusar
              </Button>
              <Button
                size="small"
                color="success"
                variant="contained"
                disabled={!canApprove}
                onClick={() => {
                  setError(null);
                  setNote('');
                  setActionTicket({ ticket, mode: 'approve' });
                }}
              >
                Aprovar
              </Button>
            </Stack>
          ) : (
            <Typography variant="caption" color="text.secondary">
              {formatWhen(ticket.parishRespondedAt || ticket.handledAt)}
            </Typography>
          )}
        </TableCell>
      </TableRow>
    );
  };

  return (
    <Stack spacing={2}>
      <Paper sx={{ p: { xs: 1.5, md: 2.5 }, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h6" fontWeight={700}>
          Atendimento / reembolsos
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Pedidos abertos pela FAQ da página do evento. Aprovar só libera o estorno quando está dentro da
          política e há saldo. Recusar avisa o suporte na hora; pedidos pendentes por mais de 2 dias também
          geram alerta automático.
        </Typography>
        {pendingCount > 0 ? (
          <Chip
            sx={{ mt: 1.5 }}
            color="warning"
            label={`${pendingCount} pendente${pendingCount > 1 ? 's' : ''} da paróquia`}
          />
        ) : null}
      </Paper>

      {isLoading ? (
        <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={28} />
        </Box>
      ) : isError ? (
        <Alert severity="error">Não foi possível carregar os pedidos.</Alert>
      ) : (
        <>
          <Paper sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
              <Typography fontWeight={700}>Pendentes</Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Participante</TableCell>
                    <TableCell>Motivo</TableCell>
                    <TableCell>Classificação</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pending.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5}>
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                          Nenhum pedido pendente.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    pending.map((t) => renderRow(t, true))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {others.length > 0 ? (
            <Paper sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
              <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography fontWeight={700}>Histórico</Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Participante</TableCell>
                      <TableCell>Motivo</TableCell>
                      <TableCell>Classificação</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Quando</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>{others.map((t) => renderRow(t, false))}</TableBody>
                </Table>
              </TableContainer>
            </Paper>
          ) : null}
        </>
      )}

      <Dialog
        open={!!actionTicket}
        onClose={() => (!busy ? setActionTicket(null) : undefined)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {actionTicket?.mode === 'approve' ? 'Aprovar reembolso' : 'Recusar pedido'}
        </DialogTitle>
        <DialogContent>
          {actionTicket ? (
            <Stack spacing={1.5} sx={{ pt: 0.5 }}>
              <Typography variant="body2">
                Participante: <strong>{actionTicket.ticket.email}</strong>
              </Typography>
              {actionTicket.mode === 'approve' ? (
                <Alert severity="info">
                  O estorno será processado agora no provedor de pagamento, se ainda houver saldo.
                </Alert>
              ) : (
                <Alert severity="warning">O suporte (meusacramento) será avisado imediatamente.</Alert>
              )}
              <TextField
                label={actionTicket.mode === 'approve' ? 'Observação (opcional)' : 'Motivo da recusa (opcional)'}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                multiline
                minRows={3}
                fullWidth
              />
              {error ? <Alert severity="error">{error}</Alert> : null}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActionTicket(null)} disabled={busy}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color={actionTicket?.mode === 'approve' ? 'success' : 'error'}
            disabled={busy}
            onClick={() =>
              actionTicket?.mode === 'approve' ? approveMutation.mutate() : rejectMutation.mutate()
            }
          >
            {busy ? <CircularProgress size={20} /> : actionTicket?.mode === 'approve' ? 'Confirmar aprovação' : 'Confirmar recusa'}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default EventWorkspaceAtendimentoTab;
